import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const normalizeApiErrorValue = (value) => {
  if (typeof value === "string") {
    const normalizedValue = value.trim();
    return normalizedValue ? [normalizedValue] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(normalizeApiErrorValue);
  }

  if (value && typeof value === "object") {
    return Object.values(value).flatMap(normalizeApiErrorValue);
  }

  return [];
};

export const extractApiErrorMessage = (error, fallback = "") => {
  const responseData = error?.response?.data;
  const apiMessageParts = [
    typeof responseData === "string" ? responseData : null,
    responseData?.message,
    responseData?.error,
    responseData?.detail,
    responseData?.errors,
  ]
    .flatMap(normalizeApiErrorValue)
    .filter(Boolean);

  if (apiMessageParts.length > 0) {
    return [...new Set(apiMessageParts)].join(". ");
  }

  const normalizedErrorMessage =
    typeof error?.message === "string" ? error.message.trim() : "";

  if (
    normalizedErrorMessage &&
    !/^Request failed with status code \d+$/.test(normalizedErrorMessage)
  ) {
    return normalizedErrorMessage;
  }

  return fallback;
};

// Validadores adicionales
const hasLowerCase = (str) => /[a-z]/.test(str);
const hasUpperCase = (str) => /[A-Z]/.test(str);
const hasNumber = (str) => /\d/.test(str);
const hasSpecialCharacter = (str) => /[!@#$%^&*]/.test(str);

// Función principal para generar el esquema Zod
export const generateZodSchema = (questions, t) => {
  if (!questions || !Array.isArray(questions)) {
    return z.object({});
  }
  // Validación de contraseña
  const passwordValidation = (field) => {
    const message =
      typeof t("ClientLayout.forms.errors.passwordLength") === "string"
        ? t("ClientLayout.forms.errors.passwordLength")
        : "La contraseña debe tener al menos 6 caracteres";
    return field.min(6, message);
  };

  // Mapa de tipos de campo y sus validaciones
  const fieldTypeMap = {
    email: (field) => {
      const message =
        typeof t("ClientLayout.forms.errors.email") === "string"
          ? t("ClientLayout.forms.errors.email")
          : "Introduce un email válido";
      return field.email(message);
    },
    terms: () => {
      const message =
        typeof t("ClientLayout.forms.errors.terms") === "string"
          ? t("ClientLayout.forms.errors.terms")
          : "Debes aceptar los términos";
      return z.boolean().refine((value) => value === true, { message });
    },
    file: () => z.any(),
    password: (field) => passwordValidation(field),
  };

  // Función para crear un campo Zod para cada pregunta
  const createZodField = (question) => {
    const requiredMessage =
      typeof t("ClientLayout.forms.errors.required") === "string"
        ? t("ClientLayout.forms.errors.required")
        : "Este campo es obligatorio";

    let field;

    // Configura el campo según el tipo de pregunta
    if (question.type === "email") {
      const emailMessage =
        typeof t("ClientLayout.forms.errors.email") === "string"
          ? t("ClientLayout.forms.errors.email")
          : "Introduce un email válido";
      field = z.string().email(emailMessage);
      if (!question.required) {
        field = field.optional(); // Aplica `optional()` solo después de la validación de email
      }
    } else if (question.type === "password") {
      field = passwordValidation(z.string());
      if (!question.required) {
        field = field.optional();
      }
    } else if (question.type === "terms") {
      const termsMessage =
        typeof t("ClientLayout.forms.errors.terms") === "string"
          ? t("ClientLayout.forms.errors.terms")
          : "Debes aceptar los términos";
      field = z
        .boolean()
        .refine((value) => value === true, { message: termsMessage });
      if (!question.required) {
        field = field.optional();
      }
    } else if (question.type === "file") {
      field = z.any();
    } else {
      // Si el tipo no es específico, usa `string` o `string` con mínimo si es requerido
      field = question.required
        ? z.string().min(1, requiredMessage)
        : z.string().optional();
    }

    return field;
  };

  // Construcción del esquema a partir de las preguntas
  const schemaObject = questions.reduce((schema, question) => {
    const zodField = createZodField(question);
    schema[question.alias] = zodField;
    if (question.type === "password") {
      schema["confirmPassword"] = z.string().optional();
    }
    return schema;
  }, {});

  // Validaciones adicionales usando `superRefine` para campos dependientes y contraseñas
  return z.object(schemaObject).superRefine((data, ctx) => {
    questions.forEach((question) => {
      if (question.depends) {
        // La API envía question.depends.id como el ID de una opción del campo padre.
        // El formulario guarda en data[parentAlias] el "value" de la opción seleccionada.
        const dependency = question.depends;
        // Encontrar la pregunta padre por contener una opción con ese ID
        const parentQuestion = questions.find(
          (q) => Array.isArray(q.options) && q.options.some((o) => o.id === dependency.id)
        );

        if (!parentQuestion) return; // Si no hay padre, no validar esta dependencia

        const selectedValue = data[parentQuestion.alias];
        const selectedOption = parentQuestion.options?.find((o) => o.value === selectedValue);
        const selectedOptionId = selectedOption?.id;

        const requiredMessage =
          typeof t("ClientLayout.forms.errors.required") === "string"
            ? t("ClientLayout.forms.errors.required")
            : "Este campo es obligatorio";

        if (
          selectedOptionId === dependency.id &&
          (!data[question.alias] || data[question.alias].length === 0) &&
          question.required
        ) {
          ctx.addIssue({ message: requiredMessage, path: [question.alias] });
        }
      }
    });

    if (
      data.password &&
      data.confirmPassword &&
      data.password !== data.confirmPassword
    ) {
      const mismatchMessage =
        typeof t("ClientLayout.forms.errors.passwordMismatch") === "string"
          ? t("ClientLayout.forms.errors.passwordMismatch")
          : "Las contraseñas no coinciden";
      ctx.addIssue({ message: mismatchMessage, path: ["confirmPassword"] });
    }
  });
};

// Valores predeterminados para cada pregunta
export const generateDefaultValues = (questions) => {
  if (!questions || !Array.isArray(questions)) return {};
  return questions.reduce((values, question) => {
    values[question.alias] = question.type === "Terms" || question.type === "terms" ? false : "";
    return values;
  }, {});
};

export const DEFAULT_LEGAL_TEXT_KEY = "Terms";

export const getQuestionLegalTextKey = (question) => {
  const raw = question?.legalTextKey;
  const normalized = typeof raw === "string" ? raw.trim() : "";
  return normalized === "" ? DEFAULT_LEGAL_TEXT_KEY : normalized;
};

export const getTermsLegalPath = (question) => {
  const key = getQuestionLegalTextKey(question);
  return key === DEFAULT_LEGAL_TEXT_KEY ? "/terms" : `/legal/${key}`;
};

const normalizeLegalTextLookupValue = (value) =>
  typeof value === "string" ? value.trim().toLocaleLowerCase() : "";

/**
 * Resuelve el `ConfigurableText` que corresponde al identificador legal de una
 * pregunta `terms`. El administrador puede asociarlo escribiendo el identificador
 * (que el formulario de políticas normaliza) o el título de la política, por lo
 * que la resolución es tolerante:
 *
 * 1. Coincidencia exacta por `type`.
 * 2. Coincidencia por `type` sin distinguir mayúsculas/minúsculas ni espacios.
 * 3. Coincidencia por título (preText) en el idioma activo o en cualquier otro.
 *
 * Devuelve `null` si no encuentra ninguno.
 */
export const resolveConfigurableTextForLegalKey = (
  configurableTexts,
  textKey,
  language,
) => {
  const texts = Array.isArray(configurableTexts)
    ? configurableTexts
    : Array.isArray(configurableTexts?.data)
      ? configurableTexts.data
      : [];

  if (texts.length === 0) return null;

  const target = normalizeLegalTextLookupValue(textKey);
  if (target === "") return null;

  const exactType = texts.find((text) => text?.type === textKey);
  if (exactType) return exactType;

  const normalizedType = texts.find(
    (text) => normalizeLegalTextLookupValue(text?.type) === target,
  );
  if (normalizedType) return normalizedType;

  const titleMatches = (text) =>
    Array.isArray(text?.preTexts) &&
    text.preTexts.some(
      (entry) => normalizeLegalTextLookupValue(entry?.text) === target,
    );

  if (typeof language === "string" && language.trim() !== "") {
    const activeLanguageNormalized = language.trim().toLocaleLowerCase();
    const localizedTitle = texts.find(
      (text) =>
        Array.isArray(text?.preTexts) &&
        text.preTexts.some(
          (entry) =>
            typeof entry?.language === "string" &&
            entry.language.toLocaleLowerCase() === activeLanguageNormalized &&
            normalizeLegalTextLookupValue(entry?.text) === target,
        ),
    );
    if (localizedTitle) return localizedTitle;
  }

  return texts.find((text) => titleMatches(text)) || null;
};

export const filterActiveFormQuestions = (questions) => {
  if (!questions || !Array.isArray(questions)) return [];

  return questions.filter(
    (question) =>
      question?.active !== false &&
      question?.visibility?.form !== false,
  );
};

export const processDefaultValues = (values) => {
  if (!values || !values.answers) return null;
  return values.answers.reduce((acc, answer) => {
    const { alias, type } = answer.question;
    let value = answer.answerText;
    if (type === "Terms") value = value === "1";
    acc[alias] = value;
    return acc;
  }, {});
};

const toFileArray = (rawValue) => {
  if (!rawValue) return [];

  if (Array.isArray(rawValue)) {
    return rawValue.filter((item) => item instanceof File);
  }

  if (typeof FileList !== "undefined" && rawValue instanceof FileList) {
    return Array.from(rawValue);
  }

  if (rawValue instanceof File) {
    return [rawValue];
  }

  return [];
};

export const collectFilesForUpload = (values, questions = []) => {
  const collected = [];
  const seenKeys = new Set();

  const pushFiles = (files, questionId = null) => {
    files.forEach((file) => {
      const uniqueKey = `${file.name}:${file.size}:${file.lastModified}`;
      if (seenKeys.has(uniqueKey)) return;
      seenKeys.add(uniqueKey);
      collected.push({ file, questionId });
    });
  };

  const fileQuestions = Array.isArray(questions)
    ? questions.filter((question) => question?.type === "file")
    : [];

  fileQuestions.forEach((question) => {
    pushFiles(toFileArray(values?.[question.alias]), question?.id ?? null);
  });

  // Compatibilidad con el campo legacy "files"
  pushFiles(toFileArray(values?.files), null);

  return collected;
};

// Cálculo de KPIs a partir de conteos por estado (paginación en servidor).
// Devuelve exactamente la misma forma que calculateKpis.
export const calculateKpisFromCounts = (countsByStateId, cardTypes) => {
  if (!countsByStateId || typeof countsByStateId !== "object") return [];
  if (!Array.isArray(cardTypes)) return [];
  const cardTypeMap = cardTypes.reduce((acc, cardType) => {
    acc[cardType.id] = {
      id: cardType.id,
      name: cardType.name,
      color: cardType.color,
      fontColor: cardType.fontColor || "#FFFFFF", // Color blanco por defecto
    };
    return acc;
  }, {});

  // Asegúrate de incluir fontColor en el resultado final
  return cardTypes.map((cardType) => ({
    id: cardType.id,
    name: cardTypeMap[cardType.id].name,
    value: (countsByStateId[cardType.id] ?? 0).toString(),
    color: cardTypeMap[cardType.id].color,
    fontColor: cardTypeMap[cardType.id].fontColor,
    border: "#A09D9E",
  }));
};
// Cálculo de KPIs
export const calculateKpis = (items, cardTypes) => {
  if (!Array.isArray(items)) return [];
  if (!Array.isArray(cardTypes)) return [];
  const cardTypeMap = cardTypes.reduce((acc, cardType) => {
    acc[cardType.id] = {
      id: cardType.id,
      name: cardType.name,
      color: cardType.color,
      fontColor: cardType.fontColor || "#FFFFFF", // Color blanco por defecto
    };
    return acc;
  }, {});

  const counts = cardTypes.reduce((acc, cardType) => {
    acc[cardType.id] = { count: 0, ...cardType };
    return acc;
  }, {});

  items.forEach((item) => {
    // Usar el ID del estado en lugar del nombre para hacer la comparación
    const stateId = item.state?.id;
    
    if (stateId && counts[stateId]) {
      counts[stateId].count++;
    }
  });

  // Asegúrate de incluir fontColor en el resultado final
  return cardTypes.map((cardType) => ({
    id: cardType.id,
    name: cardTypeMap[cardType.id].name,
    value: counts[cardType.id]?.count?.toString() || "0",
    color: cardTypeMap[cardType.id].color,
    fontColor: cardTypeMap[cardType.id].fontColor,
    border: "#A09D9E",
  }));
};

export const shouldRenderField = (question, formValues, questions, form = null) => {
  if (!questions || !Array.isArray(questions)) return true;
  
  const visitedQuestions = new Set(); // Para evitar dependencias circulares

  const resetDependentValues = (currentQuestion) => {
    // Buscar preguntas que dependan de alguna opción de currentQuestion
    const optionIds = Array.isArray(currentQuestion.options)
      ? currentQuestion.options.map((o) => o.id)
      : [];
    const dependentQuestions = questions.filter((q) =>
      q.depends && optionIds.includes(q.depends.id)
    );

    dependentQuestions.forEach((dependentQuestion) => {
      if (!visitedQuestions.has(dependentQuestion.alias)) {
        visitedQuestions.add(dependentQuestion.alias);
        
        // Resetear el valor en el formulario si está disponible
        if (form && form.setValue) {
          form.setValue(dependentQuestion.alias, undefined);
        }
        
        // También resetear directamente en formValues para compatibilidad con tests
        if (formValues && formValues.hasOwnProperty(dependentQuestion.alias)) {
          formValues[dependentQuestion.alias] = undefined;
        }
        
        // Resetear recursivamente para campos que dependan de este
        resetDependentValues(dependentQuestion);
      }
    });
  };

  const evaluateDependency = (currentQuestion) => {
    if (!currentQuestion.depends) return true;

    const dependency = currentQuestion.depends;

    // Buscar la pregunta padre por contener la opción con ID dependency.id
    const parentQuestion = questions.find(
      (q) => Array.isArray(q.options) && q.options.some((o) => o.id === dependency.id)
    );

    if (!parentQuestion) return true; // Sin padre identificado, no bloquear render

    // El valor almacenado es el "value" de la opción elegida
    const selectedValue = formValues[parentQuestion.alias];
    const selectedOption = parentQuestion.options?.find((o) => o.value === selectedValue);
    const selectedOptionId = selectedOption?.id;

    // Si la dependencia no se cumple, resetear y ocultar
    if (selectedOptionId !== dependency.id) {
      // Resetear el valor de la pregunta actual también
      if (form && form.setValue) {
        form.setValue(currentQuestion.alias, undefined);
      }
      
      // También resetear directamente en formValues para compatibilidad con tests
      if (formValues && formValues.hasOwnProperty(currentQuestion.alias)) {
        formValues[currentQuestion.alias] = undefined;
      }
      
      resetDependentValues(currentQuestion);
      return false;
    }

    // Si la dependencia se cumple, verificar si hay dependencias circulares
    if (!visitedQuestions.has(currentQuestion.alias)) {
      visitedQuestions.add(currentQuestion.alias);
      return true;
    }

    return true; // Si ya ha sido visitado, evitar recursión infinita
  };

  return evaluateDependency(question);
};
